<template>
  <div
    class="enhancements"
  >
    <booking-header-extension ref="header">
      <div class="booking-header-extension-left">
        <div class="title">
          {{ wWidth < 1024 ? tc("enhancements.complimentaryEnhancementsMobileTitle") : tc("enhancements.complimentaryEnhancementsDesktopTitle") }}
        </div>
      </div>
      <div class="booking-header-extension-right">
        <div class="count">
          {{
            tc("enhancements.addOnsAndEnhancementsAdded", {
              totalNumberOfAddOnsEnhancementsAdded: (enhancementsInRoom.length + addOnsInRoom.length),
            })
          }}
        </div>
      </div>
    </booking-header-extension>
    <div
      v-if="isBookingEditMode"
      class="editing-reservation-banner__container"
    >
      <modify-edit-banner :is-room-list="false" />
    </div>
    <div class="enhancements-wrapper">
      <div class="enhancements-content">
        <span class="enhancements-content-header-badge">{{ tc('enhancements.memberTitleForEnhancementSection').replace('{0}', userTier) }}</span>
      </div>
    </div>
    <div
      v-show="showComplimentaryPrivileges"
      class="enhancements-wrapper"
    >
      <div class="enhancements-content zero-padding">
        <div
          class="enhancements-content-header"
        >
          <div>
            <h1 class="enhancements-content-header-title">
              {{ tc('enhancements.enhancementsTitle') }}
            </h1>
            <div
              class="enhancements-content-header-description"
              v-html="tc('enhancements.enhancementsDescription')"
            />
          </div>
          <div
            role="status"
            class="enhancements-content-header-counter"
          >
            {{
              tc("enhancements.enhancementsAdded", {
                numberOfEnhancementsAdded: totalNumberOfSelectableEnhancements,
                numberOfEnhancementsAllowed: enhancementsAllowed,
              })
            }}
          </div>
        </div>
        <!-- This is the list of complementary enchancements -->
        <!-- This list contains only the available enhancements to select if the user is not in the highest tier or if the tier feature is disabled. -->
        <enhancements-list
          :enhancements-list="enhancementsList"
          :selected-enhancements="totalNumberOfSelectableEnhancements"
          :ignore-max-number="false"
          @select-enhancement="selectEnhancement"
          @remove-enhancement="removeEnhancement"
          @edit-enhancement="selectedEnhancement = $event"
        />
      </div>
    </div>
    <!-- If tier feature is enabled, this list contains all the preselected tiers for the given user -->
    <div
      v-show="hasAutoIncludedEnhancements"
      class="enhancements-wrapper"
    >
      <div
        class="enhancements-content zero-padding"
      >
        <div
          class="enhancements-content-header"
        >
          <div>
            <h1 class="enhancements-content-header-subtitle">
              {{ tc('enhancements.selectMemberBenefitTitle').replace('{0}', this.userTier) }}
            </h1>
            <div
              class="enhancements-content-header-description"
              v-html="tc('enhancements.selectMemberBenefitDescription').replace('{0}', this.userTier)"
            />
          </div>
        </div>
        <!-- This is the list of complementary enchancements -->
        <enhancements-list
          :enhancements-list="defaultSelectedEnhancements"
          :ignore-max-number="true"
          :selected-enhancements="0"
          @select-enhancement="selectEnhancement"
          @remove-enhancement="removeEnhancement"
          @edit-enhancement="selectedEnhancement = $event"
        />
      </div>
    </div>
    <!-- This is the list of addons -->
    <booking-add-ons v-if="showAddOns" />
    <transition name="slide-right">
      <enhancement-user-input
        v-if="selectedEnhancement"
        v-model="selectedEnhancement"
        :outer-activator="activator"
        @done="enhancementEdited"
        @deactivate="deactivate"
      />
    </transition>
    <transition name="fade">
      <div
        v-if="selectedEnhancement"
        class="backlight"
        @click="deactivate"
      />
    </transition>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import Tracking from '@mixins/tracking'
  import BookingHeaderExtension from '../../components/booking-header-extension/booking-header-extension.vue'
  import EnhancementsList from './components/enhancements-list.vue'
  import EnhancementsSelected from './components/enhancements-selected.vue'
  import EnhancementUserInput from './components/enhancement-user-input.vue'
  import LoadingDots from '../../../../01.base/loading-dots/loading-dots.vue'
  import ModifyEditBanner from '../../components/modify-edit-banner/modify-edit-banner.vue'
  import BookingAddOns from '../../components/booking-add-ons/booking-add-ons'

  const NO_INPUT_ENHANCEMENT = 'No input'
  export default {
    name: 'Enhancements',
    components: {
      BookingHeaderExtension,
      EnhancementsList,
      EnhancementsSelected,
      EnhancementUserInput,
      LoadingDots,
      ModifyEditBanner,
      BookingAddOns
    },
    mixins: [Tracking],
    inject: ['addOnsFeatureEnabled'],
    data: () => ({
      selectedEnhancement: null,
      minHeight: 'auto',
      enhancementsList: [],
      defaultSelectedEnhancements: [],
      activator: null,
      hasAutoIncludedEnhancements: false
    }),
    computed: {
      ...mapState('global', ['isFanClubBooking']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('cart', [
        'enhancementsInRoom',
        'allRoomsAdded',
        'getValidEnhancementsByRoomId',
        'addOnsInRoom'
      ]),
      ...mapState('cart', [
        'roomEnhancements',
        'validEnhancements',
        'cart',
        'cartPending',
      ]),
      ...mapState('booking', {
        bookingReady: 'ready',
        query: 'query',
        currentRoomId: 'currentRoomId',
      }),
      ...mapGetters('booking', [
        'currentRoom',
        'getBookingTracking',
        'getDateRangeInDays',
        'selectedHotel',
        'currentRoomIsLastRoom',
        'bookingFlow'
      ]),
      ...mapGetters('auth', ['userId', 'enhancementsAllowed', 'fanLevel']),
      ...mapGetters('modifyReservation', ['getBookingTrackingModifyReservation']),
      ...mapGetters('cart', ['getValidAddOnsByRoomId']),
      ...mapState('responsive', {
        check: 'check',
        wHeight: 'height',
        wWidth: 'width',
      }),
      ...mapState('modifyReservation', ['isBookingEditMode']),
      nextStepIndex() {
        return this.query.roomQuantity > 1 ? 'privileges-overview' : 3
      },
      isTravelAgent() {
        return !!this.query.iataNumber || this.isFanClubBooking
      },
      showComplimentaryPrivileges() {
        return this.bookingReady && this.enhancementsList.length > 0
      },
      currentRoomValidEnhancements() {
        return this.getValidEnhancementsByRoomId(this.currentRoomId)
      },
      showAddOns() {
        return this.addOnsFeatureEnabled && this.bookingReady && this.getValidAddOnsByRoomId(this.currentRoomId).length > 0
      },
      totalNumberOfSelectableEnhancements() {
        if (!this.enhancementsInRoom.length) return 0
        const selectableEnhancements = this.enhancementsList.map((e) => e.sitecoreId)
        const size = this.enhancementsInRoom.filter((e) => selectableEnhancements.indexOf(e.sitecoreId) > -1).length

        return size
      },
      userTier() {
        const currentFanLevel = this?.fanLevel?.toLowerCase()?.replace('+','') ?? 'fm'
        return this.tc(`auth.${currentFanLevel}`)
      },
    },
    watch: {
      async bookingReady(val) {
        if (val) {
          await this.validateEnhancements()
          await this.checkEnhancements()
          this.updateFooterLabel()
        }
      },
      async currentRoomId() {
        await this.checkEnhancements()
      },
      enhancementsInRoom() {
        this.updateFooterLabel()
      },
      addOnsInRoom() {
        this.updateFooterLabel()
      },
      wWidth() {
        this.calculateMinHeight()
      },
    },
    beforeRouteEnter(to, from, next) {
      next((vm) => {
        if (vm.isTravelAgent) {
          vm.nextStep('replace')
        } else if (vm.userId) {
          if (vm.bookingReady) {
            vm.updateFooterLabel()
          }
          return true
        } else {
          vm.nextStep()
        }
      })
    },
    async mounted() {
      this.calculateMinHeight()
      if (this.bookingReady) {
        await this.validateEnhancements()
        await this.checkEnhancements()
        !this.isBookingEditMode && this.hasAutoIncludedEnhancements && this.updateAutoIncludedEnhancements()
      }

      this.$nextTick(() => {
        this.$track(this.getTrackingData('booking', this.$route.query, { event: 'step5' }))
        this.$track(this.getTrackingData(this.isBookingEditMode ? 'modifyReservation' : 'booking', {
          name: 'home >> modify >> find-reservation >> details >> modify enhancements',
          hotelCode: this.$route.query.hotelCode,
          siteSection: 'find - details - modify enhancements',
        }, {
          event: 'bookingStateChange',
          bookingEngine: {
            bookingStepNumber: this.isBookingEditMode ? '2.3' : '3.3',
            bookingStepName: 'Enhancements Selection',
            bookingEngineVersion: this.isBookingEditMode ? '2' : '1',
            bookingFlow: this.bookingFlow()
          }
        }))
      })
    },
    methods: {
      ...mapActions('booking', [
        'nextStep',
        'goToStep',
        'updateNextStepButton',
        'selectRoom',
      ]),
      ...mapActions('cart', [
        'validateEnhancements',
        'addEnhancementToRoom',
        'removeEnhancementFromRoom',
      ]),
      deactivate() {
        document.querySelector('body').style.overflowY = 'auto'
        this.selectedEnhancement = null
        this.$nextTick(() => {
          this.activator = null
        })
      },
      async checkEnhancements() {
        if (this.currentRoomValidEnhancements.length > 0) {
          const allEnhancementsForRoom = this.roomEnhancements
            .filter((e) => this.filterBySynXisId(e))
            .filter((e) => this.filterByRoom(e))

          this.enhancementsList = allEnhancementsForRoom.filter((e) => this.filterByAutomaticallyIncluded(e, false))

          this.defaultSelectedEnhancements = allEnhancementsForRoom.filter((e) => this.filterByAutomaticallyIncluded(e, true))
          this.hasAutoIncludedEnhancements = Boolean(this.defaultSelectedEnhancements.length)
        } else {
          this.enhancementsList = []
          this.defaultSelectedEnhancements = []
          this.validateStep()
        }
      },
      async updateAutoIncludedEnhancements() {
        const preselectedEnhancements = []
        this.defaultSelectedEnhancements.forEach((enhancement) => {
          if (enhancement.includedAutomatically) {
            preselectedEnhancements.push(enhancement)
          }
        })

        await this.addEnhancementToRoom(preselectedEnhancements)
      },
      async updateFooterLabel() {
        if (this.enhancementsInRoom.length > 0 || this.addOnsInRoom.length > 0) {
          const label = this.isBookingEditMode
            ? `${this.tc('booking.continueTo')
            } ${
              this.tc('booking.guestDetails')}`
            : `${this.tc('booking.continueTo')} ${this.tc('steps.checkOut')}`
          this.updateNextStepButton({
            label,
            step: this.nextStepIndex,
            customClass: 'continue',
          })
        } else {
          const label = this.isBookingEditMode
            ? `${this.tc('booking.skipTo')} ${this.tc('booking.guestDetails')}`
            : `${this.tc('booking.skipTo')} ${this.tc('steps.checkOut')}`
          this.updateNextStepButton({
            label,
            step: this.nextStepIndex,
            customClass: '',
          })
        }
      },
      calculateMinHeight() {
        const headerHeight = document
          .querySelector('header.booking-header')
          .getBoundingClientRect().height
        const footerHeight = document.querySelector('.booking-footer')?.getBoundingClientRect()
          .height ?? 112
        const subheaderHeight = this.$refs.header?.$el?.getBoundingClientRect().height ?? 74
        this.minHeight = this.wHeight - headerHeight - subheaderHeight - footerHeight
      },
      filterByAutomaticallyIncluded(e, value) {
        return Boolean(e.includedAutomatically) === value
      },
      filterBySynXisId(e) {
        let result
        if (this.currentRoomValidEnhancements) {
          this.currentRoomValidEnhancements.forEach((v) => {
            if (v.code.toLowerCase() === e.synXisId.toLowerCase()) {
              result = v.code === e.synXisId
            }
          })
        }
        return result
      },
      filterByRoom(e) {
        if (e.excludedRooms && e.excludedRooms.length > 0) {
          const excludedRooms = []
          e.excludedRooms.forEach((room) => {
            if (room.HotelCode === this.selectedHotel.id) {
              excludedRooms.push(room.RoomCode)
            }
          })
          return !excludedRooms.includes(this.currentRoom.bedTypeCode)
        }
        return true
      },
      async submit() {
        await this.nextStep()
      },
      async selectEnhancement(enhancement) {
        if (enhancement.userInput === NO_INPUT_ENHANCEMENT) {
          await this.addEnhancementToRoom([enhancement])
        } else {
          this.selectedEnhancement = enhancement
          this.activator = document.getElementById(`enhancement-${enhancement.sitecoreId}`)
        }
      },
      async enhancementEdited() {
        try {
          await this.addEnhancementToRoom([this.selectedEnhancement])
          this.selectedEnhancement = null
          this.activator = null
        } catch (err) {
          this.$logger.error(err)
        }
      },
      async removeEnhancement(enhancementId) {
        await this.removeEnhancementFromRoom({ enhancementId })
        await this.checkEnhancements()
      },
      validateStep() {
        if (
          (this.currentRoomValidEnhancements.length === 0
            || this.enhancementsInRoom.length === 0) && !this.showAddOns
        ) {
          if (Number(this.query.roomQuantity) === 1) {
            this.goToStep(this.nextStepIndex)
          } else if (!this.currentRoomIsLastRoom) {
            this.selectRoom(this.currentRoomId + 1)
          } else {
            this.$router.push({
              name: this.nextStepIndex,
              query: {
                ...this.$route.query,
              },
            })
          }
        }
      },
    },
  }
</script>
